// src/App.jsx

import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Import global CSS
import './App.css';

// --- Core Components (loaded immediately) ---
// Corrected the AppLayout import to use the separate component file.
import AppLayout from './components/AppLayout';
import ProtectedRoute from './components/ProtectedRoute';
import TokenHandler from './components/TokenHandler';

// --- Core Pages (loaded immediately for fast initial experience) ---
import HomePage from './pages/HomePage';
import LoginSignUpPage from './pages/LoginSignUpPage';

// --- LAZY-LOADED COMPONENTS (Code-splitting for performance) ---
// These components will only be downloaded by the browser when the user navigates to them.
const LostItemsPage = React.lazy(() => import('./pages/LostItemsPage'));
const NewItemPage = React.lazy(() => import('./pages/NewItemPage'));
const UserProfile = React.lazy(() => import('./pages/UserProfile'));
const ContactUsPage = React.lazy(() => import('./pages/ContactUsPage'));
const Notifications = React.lazy(() => import('./components/Notifications'));
const Chat = React.lazy(() => import('./components/Chat'));
const VerifyOwnership = React.lazy(() => import('./components/VerifyOwnership'));
const Messages = React.lazy(() => import('./components/Messages'));
const MatchesPage = React.lazy(() => import('./pages/MatchesPage'));

// --- A simple loading spinner component to show while lazy components load ---
const LoadingSpinner = () => (
  <div style={{ textAlign: 'center', padding: '4rem', fontSize: '1.2rem' }}>
    Loading...
  </div>
);

// --- Initialize React Query Client ---
const queryClient = new QueryClient();

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <Router>
          {/* TokenHandler is non-visual and can be placed here */}
          <TokenHandler />

          {/* The AppLayout provides the NavBar and Footer to all pages */}
          <AppLayout>
            {/* Suspense provides a fallback UI while lazy components are loading */}
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/contact" element={<ContactUsPage />} />
                <Route path="/login-signup" element={<LoginSignUpPage />} />

                {/* Protected Routes */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/lost-items" element={<LostItemsPage />} />
                  <Route path="/verify/:itemId" element={<VerifyOwnership />} />
                  <Route path="/chat/:conversationId" element={<Chat />} />
                  <Route path="/new-item" element={<NewItemPage />} />
                  <Route path="/profile" element={<UserProfile />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/messages" element={<Messages />} />
                  <Route path="/item/:itemId/matches" element={<MatchesPage />} />
                </Route>
              </Routes>
            </Suspense>
          </AppLayout>
        </Router>
      </QueryClientProvider>
    </AuthProvider>
  );
}

export default App;